import type {
  Contract,
  ContractSummaryPayload,
  DashboardPayload,
} from "src/app/pages/dashboard-page/services/dashboard.service";
import type { EpkFamilyPerformancePayload } from "src/app/pages/dashboard-page/models/epk-family-performance.models";
import { ContractCategory, ProductType } from "../pages/contracts/models/contracts.models";
import { ContractFixture } from "./contracts/contract-fixtures";

const GREETING_TITLE = "Hei, Ola Nordmann";

function roundToTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Return percentage relative to the cost basis (balance minus accumulated return).
 */
function calculateReturnPct(balance: number, totalReturn: number): number {
  const costBasis = balance - totalReturn;

  return costBasis <= 0 ? 0 : roundToTwoDecimals((totalReturn / costBasis) * 100);
}

function toDashboardContract(fixture: ContractFixture, type: ContractCategory): Contract {
  const totalReturn = fixture.contract.totalReturn ?? 0;
  const totalBalance = fixture.contract.totalBalance ?? 0;

  return {
    id: fixture.contract.id,
    name: fixture.contract.name,
    totalBalance: fixture.contract.totalBalance,
    totalReturn,
    totalReturnPct: calculateReturnPct(totalBalance, totalReturn),
    includeInCalculation: fixture.includeInCalculation,
    type,
    productType: fixture.productType ?? null,
    detailsLink: fixture.contract.detailsLink,
  };
}

function sumIncludedBalance(contracts: readonly Contract[]): number {
  return contracts.filter((c) => c.includeInCalculation).reduce((total, c) => total + (c.totalBalance ?? 0), 0);
}

function sumIncludedReturn(contracts: readonly Contract[]): number {
  return contracts.filter((c) => c.includeInCalculation).reduce((total, c) => total + c.totalReturn, 0);
}

/**
 * Derives the /contracts/summary payload from the selected contract fixtures, so the dashboard
 * rollups are always the sum of what Avtaler lists. Folketrygden and in-transit (untyped)
 * contracts belong to neither dashboard bucket.
 */
export function deriveContractSummary(fixtures: readonly ContractFixture[]): ContractSummaryPayload {
  const employerContracts = fixtures
    .filter((f) => f.contract.type === ContractCategory.Employer)
    .map((f) => toDashboardContract(f, ContractCategory.Employer));
  const userContracts = fixtures
    .filter((f) => f.contract.type === ContractCategory.UserSavings)
    .map((f) => toDashboardContract(f, ContractCategory.UserSavings));

  const employerContractsBalance = sumIncludedBalance(employerContracts);
  const employerContractReturn = sumIncludedReturn(employerContracts);
  const userContractsBalance = sumIncludedBalance(userContracts);
  const userContractReturn = sumIncludedReturn(userContracts);
  const totalBalance = employerContractsBalance + userContractsBalance;
  const totalReturn = employerContractReturn + userContractReturn;

  return {
    employerContracts,
    userContracts,
    employerContractsBalance,
    employerContractReturn,
    employerContractsReturnPct: calculateReturnPct(employerContractsBalance, employerContractReturn),
    userContractsBalance,
    userContractReturn,
    userContractsReturnPct: calculateReturnPct(userContractsBalance, userContractReturn),
    totalBalance,
    totalReturn,
    totalReturnPct: calculateReturnPct(totalBalance, totalReturn),
    errors: [],
  };
}

/** Derives the /user/summary payload from the same fixtures, so the two endpoints cannot disagree. */
export function deriveUserSummary(fixtures: readonly ContractFixture[]): DashboardPayload {
  const summary = deriveContractSummary(fixtures);

  return {
    title: GREETING_TITLE,
    totalBalance: summary.totalBalance,
    totalReturnPct: summary.totalReturnPct,
  };
}

/** Backend `SavingsEngagementType` names the backend counts as EPK family for /contracts/epk-family/performance. */
const EPK_FAMILY_PRODUCT_TYPES: ReadonlySet<string> = new Set<string>([
  ProductType.Epk,
  ProductType.EpkFleksibel,
  ProductType.Pkb,
  ProductType.EpkFleksibelPkb,
]);

/** Backend `SavingsEngagementType` names the backend counts as public or hybrid pension for /contracts/summary. */
const PUBLIC_OR_HYBRID_PRODUCT_TYPES: ReadonlySet<string> = new Set<string>([
  "PUBLIC_PENSION",
  "HYBRID_MED_GARANTI",
  "HYBRID_PENSJONSBEVIS",
  "YTP",
]);

/** The dashboard facts on /contracts/summary. */
export type ContractSummaryFacts = Required<
  Pick<
    ContractSummaryPayload,
    | "hasContractUnderPayout"
    | "hasEpkFamilyContract"
    | "hasPaidUpPolicyEligibleForFmi"
    | "hasPensionPayouts"
    | "hasPublicOrHybridPensionContract"
    | "ipsSavingsAboveMaximum"
  >
>;

/**
 * Derives the dashboard facts on /contracts/summary from the same fixtures. The fixtures have no payout or
 * savings agreement data, so those facts are fixed: nothing under payout, no pension payouts and no IPS
 * savings above the limit.
 */
export function deriveContractSummaryFacts(fixtures: readonly ContractFixture[]): ContractSummaryFacts {
  const hasProductType = (productTypes: ReadonlySet<string>): boolean =>
    fixtures.some((f) => !!f.productType && productTypes.has(f.productType));

  return {
    hasContractUnderPayout: false,
    hasEpkFamilyContract: hasProductType(EPK_FAMILY_PRODUCT_TYPES),
    hasPaidUpPolicyEligibleForFmi: fixtures.some((f) => f.productType === "FRIPOLISE"),
    hasPensionPayouts: false,
    hasPublicOrHybridPensionContract: hasProductType(PUBLIC_OR_HYBRID_PRODUCT_TYPES),
    ipsSavingsAboveMaximum: false,
  };
}

/** Illustrative deposits per contract over the period; the fixtures have no transactions. */
const MOCK_PAID_IN_PER_CONTRACT = 18000;

/** Illustrative fund, one of the profiles in the FMS `revenueModal.chart.data`. */
const MOCK_FULLY_ALLOCATED_FUND_NAME = "Storebrand Balansert";

/**
 * Derives the /contracts/epk-family/performance payload from the same fixtures, so the revenue modal
 * matches the EPK-family contracts listed elsewhere. The balance and return are the contracts' own.
 */
export function deriveEpkFamilyPerformance(fixtures: readonly ContractFixture[]): EpkFamilyPerformancePayload {
  const contracts = fixtures.filter((f) => !!f.productType && EPK_FAMILY_PRODUCT_TYPES.has(f.productType));

  if (contracts.length === 0) {
    return {
      contractCount: 0,
      marketValue: null,
      totalReturn: null,
      totalReturnPct: null,
      paidIn: 0,
      fullyAllocatedFundName: null,
      errors: null,
    };
  }

  const marketValue = contracts.reduce((total, f) => total + (f.contract.totalBalance ?? 0), 0);
  const totalReturn = contracts.reduce((total, f) => total + (f.contract.totalReturn ?? 0), 0);

  return {
    contractCount: contracts.length,
    marketValue,
    totalReturn,
    totalReturnPct: calculateReturnPct(marketValue, totalReturn),
    paidIn: contracts.length * MOCK_PAID_IN_PER_CONTRACT,
    fullyAllocatedFundName: MOCK_FULLY_ALLOCATED_FUND_NAME,
    errors: null,
  };
}
